import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SeoSocial } from "@/components/SeoSocial";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { socialLinks } from "@/lib/social-config";
import "./portfolio.css";
import "./portfolio-pages.css";

const navigation = [
  { href: "/", label: "Accueil" },
  { href: "/services", label: "Services" },
  { href: "/projets", label: "Projets" },
  { href: "/a-propos", label: "À propos" },
  { href: "/contact", label: "Contact" },
];

export function PortfolioHeader({ studio = false }: { studio?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        menuButton.current?.focus();
      }
      if (event.key !== "Tab") return;
      const links = Array.from(menu.current?.querySelectorAll<HTMLAnchorElement>("a") ?? []);
      const focusable = [menuButton.current, ...links].filter(Boolean) as HTMLElement[];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    const onResize = () => { if (window.innerWidth >= 900) setOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  return (
    <header className={`pf-header${open ? " is-open" : ""}`}>
      <Link className="pf-monogram" to="/" aria-label={studio ? "Tokam Darius, accueil" : "TD : Tokam Darius, accueil"}>{studio ? "TOKAM DARIUS" : <>TD<span aria-hidden="true">.</span></>}</Link>
      <span className="pf-header-rule" aria-hidden="true" />
      <nav className="pf-desktop-nav" aria-label="Navigation principale">
        {(studio ? [navigation[0], navigation[2], navigation[1], navigation[3], { href: "/blog", label: "Journal" }] : navigation).map(link => <NavLink key={link.href} to={link.href} end={link.href === "/"}>{link.label}</NavLink>)}
      </nav>
      <Link className="pf-header-journal pf-label" to={studio ? "/contact" : "/blog"}>{studio ? "Parlons de votre projet" : "Le journal"} <ArrowUpRight size={14} /></Link>
      <button ref={menuButton} className="pf-menu-button" aria-expanded={open} aria-controls="portfolio-mobile-menu" aria-label={open ? "Fermer le menu" : "Ouvrir le menu"} onClick={() => setOpen(!open)}>
        {open ? <X size={25} /> : <Menu size={25} />}
      </button>
      {open && (
        <nav ref={menu} id="portfolio-mobile-menu" className="pf-mobile-menu" aria-label="Navigation mobile">
          <span className="pf-label">Explorer le portfolio</span>
          {navigation.map((link, i) => <NavLink key={link.href} to={link.href} end={link.href === "/"} onClick={() => setOpen(false)}><small>0{i + 1}</small>{link.label}<ArrowUpRight /></NavLink>)}
          <Link to="/blog" onClick={() => setOpen(false)}><small>06</small>Le journal<ArrowUpRight /></Link>
          <span className="pf-label">Jonquière, Québec · Canada</span>
        </nav>
      )}
    </header>
  );
}

export function PortfolioFooter({ studio = false }: { studio?: boolean } = {}) {
  return (
    <footer className="pf-footer">
      <Link className="pf-monogram" to="/" aria-label={studio ? "Tokam Darius, retour à l'accueil" : "TD : Tokam Darius, retour à l'accueil"}>{studio ? "TOKAM DARIUS" : <>TD<span aria-hidden="true">.</span></>}</Link>
      <p>© {new Date().getFullYear()} Tokam Darius</p>
      <div>{[socialLinks.github, socialLinks.linkedin].filter(social => social.available).map(social => <a key={social.label} href={social.url} target="_blank" rel="noopener noreferrer">{social.label}<ArrowUpRight size={14} /></a>)}<Link to="/blog">Journal<ArrowUpRight size={14} /></Link></div>
      <a className="pf-back-top" href="#page-top">Retour en haut <ArrowUpRight size={16} /></a>
    </footer>
  );
}

export function PortfolioCallout() {
  return <section className="pf-page-callout"><div><span className="pf-label">Construisons la suite</span><h2>Votre projet<br /><span className="pf-serif">commence ici.</span></h2></div><div><p>Un besoin, un outil à faire évoluer ou une collaboration à proposer ? Décrivez-moi ce que vous avez en tête.</p><Link className="pf-button" to="/contact">Parlons de votre projet <ArrowUpRight size={20} /></Link></div></section>;
}

export function PortfolioLayout({ title, description, path, children, studio = false, carnet = false, journal = false }: { title: string; description: string; path: string; children: ReactNode; studio?: boolean; carnet?: boolean; journal?: boolean }) {
  return (
    <div className={`portfolio-home pf-inner-page${studio ? " pf-services-studio" : ""}${carnet ? " pf-projects-carnet" : ""}${journal ? " pf-journal-page" : ""}`} id="page-top">
      <Helmet><title>{title}</title><meta name="description" content={description} /><link rel="canonical" href={`https://tokamdarius.ca${path}`} /></Helmet>
      <SeoSocial title={title} description={description} url={`https://tokamdarius.ca${path}`} />
      <a className="pf-skip-link" href="#portfolio-content">Aller au contenu</a>
      <PortfolioHeader studio={studio || carnet || journal} />
      <main id="portfolio-content" tabIndex={-1}>{children}</main>
      <PortfolioFooter studio={studio || carnet || journal} />
    </div>
  );
}
